import { css, html, LitElement, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import type { TemplateResult } from 'lit';

/**
 * Minimal shapes for the parts of the custom elements manifest
 * (`custom-elements.json`, schema 1.0.0) the API reference renders.
 */
export interface CemAttribute {
  name: string;
  description?: string;
  type?: { text?: string };
  default?: string;
  fieldName?: string;
}

export interface CemMember {
  kind?: string;
  name: string;
  description?: string;
  type?: { text?: string };
  default?: string;
  attribute?: string;
  privacy?: string;
  static?: boolean;
}

export interface CemNamedItem {
  name: string;
  description?: string;
  default?: string;
}

export interface CemDeclaration {
  kind?: string;
  tagName?: string;
  summary?: string;
  attributes?: CemAttribute[];
  members?: CemMember[];
  events?: CemNamedItem[];
  slots?: CemNamedItem[];
  cssParts?: CemNamedItem[];
  cssProperties?: CemNamedItem[];
}

interface ApiRow {
  name: string;
  type?: string;
  defaultValue?: string;
  description?: string;
}

/**
 * Auto-generated API reference for one custom element, rendered from its
 * custom elements manifest declaration. Shows attributes/properties, events,
 * slots, CSS parts and CSS custom properties - whichever the element defines.
 */
export class DemoApiDocs extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    webmodule-details::part(base) {
      background: var(--webmodule-panel-background-color);
    }

    .tag {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-small);
      font-weight: var(--webmodule-font-weight-semibold, 600);
      color: var(--webmodule-color-primary-700);
    }

    h4 {
      margin: var(--webmodule-spacing-large) 0 var(--webmodule-spacing-x-small) 0;
      font-size: var(--webmodule-font-size-small);
      font-weight: var(--webmodule-font-weight-semibold, 600);
      color: var(--webmodule-color-neutral-600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    h4:first-of-type {
      margin-top: 0;
    }

    .table-scroll {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--webmodule-font-size-small);
    }

    th {
      text-align: left;
      padding: var(--webmodule-spacing-x-small) var(--webmodule-spacing-medium) var(--webmodule-spacing-x-small) 0;
      border-bottom: solid 1px var(--webmodule-color-neutral-200);
      color: var(--webmodule-color-neutral-500);
      font-weight: var(--webmodule-font-weight-normal, 400);
      white-space: nowrap;
    }

    td {
      padding: var(--webmodule-spacing-x-small) var(--webmodule-spacing-medium) var(--webmodule-spacing-x-small) 0;
      border-bottom: solid 1px var(--webmodule-color-neutral-100);
      vertical-align: top;
      color: var(--webmodule-color-neutral-700);
    }

    td.name {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-x-small);
      color: var(--webmodule-color-neutral-900);
      white-space: nowrap;
    }

    td.type,
    td.default {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-x-small);
      color: var(--webmodule-color-neutral-500);
      max-width: 220px;
    }

    .empty {
      color: var(--webmodule-color-neutral-500);
      font-size: var(--webmodule-font-size-small);
    }
  `;

  /** The element tag the reference is for, e.g. 'webmodule-button'. */
  @property() tag = '';
  /** The manifest declaration backing the reference. */
  @property({ attribute: false }) declaration: CemDeclaration | null = null;

  render() {
    const decl = this.declaration;

    if (!decl) {
      return nothing;
    }

    // Attributes cover the reflected surface; add documented public fields
    // that have no attribute (e.g. `.columns` on the table) as JS-only rows.
    const attributeRows: ApiRow[] = (decl.attributes ?? []).map(attr => ({
      name: attr.fieldName && attr.fieldName !== attr.name ? `${attr.name} · .${attr.fieldName}` : attr.name,
      type: attr.type?.text,
      defaultValue: attr.default,
      description: attr.description
    }));
    const attributeFields = new Set((decl.attributes ?? []).map(attr => attr.fieldName ?? attr.name));
    const propertyRows: ApiRow[] = (decl.members ?? [])
      .filter(
        member =>
          member.kind === 'field' &&
          !member.static &&
          !member.attribute &&
          !attributeFields.has(member.name) &&
          (member.privacy ?? 'public') === 'public' &&
          !!member.description
      )
      .map(member => ({
        name: `.${member.name}`,
        type: member.type?.text,
        defaultValue: member.default,
        description: member.description
      }));

    const events: ApiRow[] = (decl.events ?? []).map(item => ({ name: item.name, description: item.description }));
    const slots: ApiRow[] = (decl.slots ?? []).map(item => ({
      name: item.name === '' ? '(default)' : item.name,
      description: item.description
    }));
    const cssParts: ApiRow[] = (decl.cssParts ?? []).map(item => ({
      name: item.name,
      description: item.description
    }));
    const cssProperties: ApiRow[] = (decl.cssProperties ?? []).map(item => ({
      name: item.name,
      defaultValue: item.default,
      description: item.description
    }));
    const hasAnything =
      attributeRows.length +
        propertyRows.length +
        events.length +
        slots.length +
        cssParts.length +
        cssProperties.length >
      0;

    return html`
      <webmodule-details>
        <span slot="summary" class="tag">&lt;${this.tag}&gt;</span>
        ${hasAnything ? nothing : html`<p class="empty">No documented API surface.</p>`}
        ${this.renderTable('Attributes & properties', attributeRows, true)}
        ${this.renderTable('Properties (JS only)', propertyRows, true)} ${this.renderTable('Events', events, false)}
        ${this.renderTable('Slots', slots, false)} ${this.renderTable('CSS parts', cssParts, false)}
        ${this.renderTable('CSS custom properties', cssProperties, false)}
      </webmodule-details>
    `;
  }

  private renderTable(title: string, rows: ApiRow[], withTypes: boolean): TemplateResult | typeof nothing {
    if (!rows.length) {
      return nothing;
    }

    const showDefaults = withTypes && rows.some(row => row.defaultValue !== undefined);

    return html`
      <h4>${title}</h4>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              ${withTypes ? html`<th>Type</th>` : nothing} ${showDefaults ? html`<th>Default</th>` : nothing}
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(
              row => html`
                <tr>
                  <td class="name">${row.name}</td>
                  ${withTypes ? html`<td class="type">${row.type ?? ''}</td>` : nothing}
                  ${showDefaults ? html`<td class="default">${row.defaultValue ?? ''}</td>` : nothing}
                  <td>${row.description ?? ''}</td>
                </tr>
              `
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}
